import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShieldAlert } from 'lucide-react';
import { Logo } from './ui/Logo';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('platform_admin' | 'admin' | 'business' | 'customer' | 'tenant_manager' | 'employee')[];
}

/**
 * ProtectedRoute Component
 * 
 * Handles authentication and role-based access control using Supabase.
 * Fetches user role from 'users' table via AuthContext.
 * 
 * @example
 * <ProtectedRoute allowedRoles={['admin']}>
 *   <AdminDashboard />
 * </ProtectedRoute>
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, loading, status, isRetrying, resolvedRole, signOut } = useAuth();
  
  // 3. DEBUG LOGS (as requested by rule 7)
  React.useEffect(() => {
    if (user) {
      console.log(`[AuthDebug] auth uid: ${user.id} | fetched profile role: ${profile?.role || 'none'} | final resolved role: ${resolvedRole || 'none'} | navigation target: ${location.pathname}`);
    }
  }, [user, profile, resolvedRole, location]);

  // 1. ENSURE LOADING IS RESPECTED: Wait for initial auth session to hydrate
  // We show a unified loader while 'loading' is true to avoid state jitters
  if (loading || status === 'authenticated_loading_profile') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0A1428] p-4 text-center">
        <div className="mb-6 scale-150 transform transition-all animate-pulse">
          <Logo variant="light" showText={false} className="h-16" />
        </div>
        <div className="w-8 h-8 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin mb-4" />
        <h2 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest leading-none mb-2">
          ServiceHub Africa
        </h2>
        <p className="text-slate-500 font-bold max-w-xs text-[10px] uppercase tracking-[0.2em]">
          Connecting securely to workspace...
        </p>
        
        {/* HYPER-FAILSAFE: Allow manual override if hydration hangs */}
        <div className="mt-8 flex flex-col items-center">
          <div className="flex gap-4">
            <button 
              onClick={() => window.location.reload()}
              className="text-xs text-blue-600 font-bold hover:underline opacity-70 hover:opacity-100 transition-opacity"
            >
              Refresh
            </button>

          </div>
        </div>
      </div>
    );
  }

  // 2. ONLY REDIRECT AFTER LOADING COMPLETES: Prevent race condition on refresh
  // If loading is finished AND we have no user, redirect to login
  if (!user) {
    console.log('Auth: no session found in ProtectedRoute, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // 4. MISSING PROFILE OR ROLE CHECK
  // Since we use ONLY database user_profiles role as requested, if we finished loading but have no profile, show error
  if (status === 'missing_profile' || !profile || !resolvedRole) {
    console.warn(`[AuthGuard] Access Denied to ${location.pathname}. Database profile is missing or unresolved for UID ${user.id}.`);
    
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4 text-center">
        <div className="bg-white p-8 rounded-3xl shadow-xl max-w-md w-full border border-red-100">
          <h2 className="text-xl font-black text-red-600 mb-2 uppercase tracking-widest">Profile Missing</h2>
          <p className="text-slate-500 text-sm mb-8 leading-relaxed">
            We couldn't synchronize your account details from the service registry. 
            This can happen after first registration. Please try refreshing or logging back in.
          </p>
          <div className="flex flex-col gap-3">
            <button 
              onClick={() => window.location.reload()} 
              className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black shadow-lg shadow-blue-500/20"
            >
              Refresh App
            </button>
            <button 
              onClick={() => signOut()} 
              className="w-full py-4 bg-slate-100 text-slate-500 rounded-2xl font-black"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 5. ASYNC ROLE PROTECTION: Once profile is available, enforce role constraints
  if (profile.is_suspended) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4 text-center">
        <div className="bg-white p-8 rounded-3xl shadow-xl max-w-md w-full border border-red-100">
          <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-xl font-black text-red-600 mb-2 uppercase tracking-widest">Account Suspended</h2>
          <p className="text-slate-500 text-sm mb-8 leading-relaxed">
            Your account has been suspended for violating our community guidelines or terms of service. 
            If you believe this is an error, please contact support.
          </p>
          <button 
            onClick={() => signOut()} 
            className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black shadow-lg shadow-black/20"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (allowedRoles && !allowedRoles.includes(resolvedRole as any)) {
    console.log(`Auth: Role mismatch for UID ${user.id}. Required: ${allowedRoles.join(',')}, Found Resolved: ${resolvedRole}`);
    
    const roleLabels = {
      platform_admin: 'Platform Owner',
      admin: 'Platform Administrator',
      business: 'Business Dealer',
      customer: 'Customer',
      tenant_manager: 'Market Manager',
      employee: 'Staff Member'
    };
    
    const dashboardMap = {
      platform_admin: '/admin',
      admin: '/admin',
      business: '/business',
      customer: '/marketplace',
      tenant_manager: '/admin',
      employee: '/employee'
    };

    // AUTOMATIC REDIRECTION: If they are on the wrong dashboard, just send them to the right one
    const correctPath = dashboardMap[resolvedRole as keyof typeof dashboardMap];
    if (correctPath && correctPath !== location.pathname) {
      console.log(`Auth: Redirecting ${resolvedRole} to their correct dashboard: ${correctPath}`);
      return <Navigate to={correctPath} replace />;
    }
    
    // Explicit access denied if no mapping or redirect loop
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4 text-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6 font-medium">This section is for {allowedRoles.map(r => roleLabels[r as keyof typeof roleLabels]).join(' or ')} only.</p>
          <button onClick={() => navigate(correctPath || '/')} className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg">Go to My Dashboard</button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
